import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Content, Lesson, Unit } from '@/types'
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  FileText,
  PenTool,
  AlertCircle,
  Filter
} from 'lucide-react'

interface ContentWithRelations extends Content {
  lesson: Lesson & { unit: Unit }
  exercises_count: number
}

export default function ContentsList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [contents, setContents] = useState<ContentWithRelations[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  // Filtros persistidos en URL search params
  const filterUnit = searchParams.get('unit') || ''
  const filterLesson = searchParams.get('lesson') || ''

  const setFilterUnit = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('unit', value)
    } else {
      params.delete('unit')
    }
    params.delete('lesson') // Reset lesson when unit changes
    setSearchParams(params, { replace: true })
  }

  const setFilterLesson = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('lesson', value)
    } else {
      params.delete('lesson')
    }
    setSearchParams(params, { replace: true })
  }

  // Build query string for passing filters to edit/view links
  const filterQuery = searchParams.toString() ? `?${searchParams.toString()}` : ''
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; content: Content | null }>({
    open: false,
    content: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [contentsRes, unitsRes, lessonsRes] = await Promise.all([
        supabase
          .from('contents')
          .select(`
            *,
            lesson:lessons(*, unit:units(*)),
            exercises:exercises(count)
          `)
          .order('lesson_id', { ascending: true })
          .order('content_number', { ascending: true }),
        supabase.from('units').select('*').order('unit_number', { ascending: true }),
        supabase.from('lessons').select('*').order('unit_id', { ascending: true }).order('order_index', { ascending: true })
      ])

      if (contentsRes.error) throw contentsRes.error
      if (unitsRes.error) throw unitsRes.error
      if (lessonsRes.error) throw lessonsRes.error

      const contentsWithCount = (contentsRes.data || []).map((c: any) => ({
        ...c,
        title: c.title || c.title_es || c.title_en || c.name_es || c.name_en || '',
        description: c.description || c.description_es || c.description_en || '',
        order_num: c.order_num ?? c.content_number ?? 0,
        exercises_count: c.exercises?.[0]?.count || 0
      }))

      const mappedUnits = (unitsRes.data || []).map((u: any) => ({
        ...u,
        title: u.title || u.title_es || u.title_en || u.name_es || u.name_en || '',
        order_num: u.order_num ?? u.unit_number ?? 0,
      }))

      const mappedLessons = (lessonsRes.data || []).map((l: any) => ({
        ...l,
        title: l.title || l.title_es || l.title_en || l.name_es || l.name_en || '',
        order_num: l.order_num ?? l.order_index ?? l.lesson_number ?? 0,
      }))

      setContents(contentsWithCount)
      setUnits(mappedUnits)
      setLessons(mappedLessons)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal.content) return

    setIsDeleting(true)
    try {
      const { error } = await supabase
        .from('contents')
        .delete()
        .eq('id', deleteModal.content.id)

      if (error) throw error

      setContents(contents.filter(c => c.id !== deleteModal.content?.id))
      setDeleteModal({ open: false, content: null })
    } catch (error) {
      console.error('Error deleting content:', error)
      alert('Error al eliminar el contenido. Asegúrate de que no tenga ejercicios asociados.')
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredLessons = filterUnit
    ? lessons.filter(l => l.unit_id === filterUnit)
    : lessons

  const filteredContents = contents.filter(content => {
    const matchesSearch = (content.title || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesUnit = !filterUnit || content.lesson?.unit?.id === filterUnit
    const matchesLesson = !filterLesson || content.lesson_id === filterLesson
    return matchesSearch && matchesUnit && matchesLesson
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contenidos</h1>
          <p className="text-gray-600">Gestiona los contenidos del curso</p>
        </div>
        <Link
          to={`/admin/contents/new${filterQuery}`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuevo Contenido
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar contenidos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={filterUnit}
            onChange={(e) => setFilterUnit(e.target.value)}
            className="pl-10 pr-8 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] appearance-none bg-white"
          >
            <option value="">Todas las unidades</option>
            {units.map(unit => (
              <option key={unit.id} value={unit.id}>
                Unidad {unit.order_num}
              </option>
            ))}
          </select>
        </div>
        <select
          value={filterLesson}
          onChange={(e) => setFilterLesson(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] bg-white"
        >
          <option value="">Todas las lecciones</option>
          {filteredLessons.map(lesson => (
            <option key={lesson.id} value={lesson.id}>
              Lección {lesson.order_num}: {lesson.title}
            </option>
          ))}
        </select>
      </div>

      {/* Contents Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Orden
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contenido
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lección
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ejercicios
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredContents.length > 0 ? (
                filteredContents.map((content) => (
                  <tr key={content.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 font-semibold">
                        {content.order_num}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-100">
                          <FileText className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{content.title}</p>
                          <p className="text-sm text-gray-500">Contenido</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <p className="text-gray-900">Lección {content.lesson?.order_num ?? (content.lesson as any)?.order_index ?? ''}</p>
                        <p className="text-gray-500">Unidad {content.lesson?.unit?.order_num ?? ''}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <PenTool className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{content.exercises_count} ejercicios</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        content.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {content.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/contents/${content.id}${filterQuery}`}
                          className="p-2 text-gray-500 hover:text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
                          title="Visualizar"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/admin/contents/${content.id}/edit${filterQuery}`}
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setDeleteModal({ open: true, content })}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No se encontraron contenidos</p>
                    <Link
                      to="/admin/contents/new"
                      className="text-[#0055A4] hover:underline text-sm mt-2 inline-block"
                    >
                      Crear primer contenido
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-red-100">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Eliminar Contenido</h3>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar el contenido "{deleteModal.content?.title}"?
              Esta acción no se puede deshacer y eliminará todos los ejercicios asociados.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, content: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
